import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { CalendarBlank, Plus, Trash, Clock } from '@phosphor-icons/react';

export default function ScheduledBroadcastPage() {
  const { selectedBot, bots } = useOutletContext();
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [buttons, setButtons] = useState([]);
  const [mediaType, setMediaType] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [requestMobile, setRequestMobile] = useState(false);
  const [showBtnLabel, setShowBtnLabel] = useState(true);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [submitting, setSubmitting] = useState(false);

  const bot = bots.find(b => b.id === selectedBot);
  const fetchScheduled = () => {
    const params = {};
    if (selectedBot) params.bot_id = selectedBot;
    API.get('/scheduled-broadcasts', { params }).then(r => setScheduled(r.data.scheduled_broadcasts || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(fetchScheduled, [selectedBot]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBot) return toast.error('Select a bot first');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('bot_id', selectedBot);
      if (text1) formData.append('text_1', text1);
      if (text2) formData.append('text_2', text2);
      formData.append('inline_buttons', JSON.stringify(buttons));
      formData.append('request_mobile', requestMobile.toString());
      formData.append('show_button_label', showBtnLabel.toString());
      if (mediaType) formData.append('media_type', mediaType);
      if (mediaFile) formData.append('media_file', mediaFile);
      if (mediaType === 'poll') {
        formData.append('poll_question', pollQuestion);
        formData.append('poll_options', JSON.stringify(pollOptions.filter(o => o.trim())));
      }
      formData.append('scheduled_at', scheduledAt);
      await API.post('/scheduled-broadcasts', formData);
      toast.success('Broadcast scheduled');
      setText1(''); setText2(''); setButtons([]); setMediaType(''); setMediaFile(null);
      setScheduledAt(''); setRequestMobile(false); setPollQuestion(''); setPollOptions(['', '']);
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const deleteScheduled = async (id) => {
    try { await API.delete(`/scheduled-broadcasts/${id}`); toast.success('Deleted'); fetchScheduled(); } catch { toast.error('Failed'); }
  };

  if (!selectedBot) {
    return (
      <div className="space-y-4" data-testid="scheduled-broadcast-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Scheduled Broadcast</h1>
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">Select a bot from the sidebar</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scheduled-broadcast-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Scheduled Broadcast</h1>
        <p className="text-sm text-muted-foreground mt-1">Schedule broadcasts for {bot?.bot_name}</p>
      </div>
      <div className="rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
        <p className="text-sm"><span className="text-lg mr-1.5">📋</span><strong className="text-blue-700 dark:text-blue-300">Order:</strong> <span className="text-blue-600 dark:text-blue-400">🖼️ Media → 📝 Text 1 → 📝 Text 2 + Buttons</span></p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
        <div className="border border-border rounded-md p-5 space-y-4">
          <h4 className="font-medium text-base flex items-center gap-2"><Clock size={18} /> Schedule Time (IST)</h4>
          <Input data-testid="schedule-datetime" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} required />
          <div className="space-y-1.5">
            <Label>Media Type</Label>
            <select data-testid="schedule-media-type" value={mediaType} onChange={e => setMediaType(e.target.value)} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background">
              <option value="">None</option>
              <option value="photo">📷 Photo</option>
              <option value="gif">🎞️ GIF</option>
              <option value="video">🎬 Video</option>
              <option value="poll">📊 Poll</option>
            </select>
          </div>
          {mediaType && mediaType !== 'poll' && <Input data-testid="schedule-media-file" type="file" onChange={e => setMediaFile(e.target.files[0])} />}
          {mediaType === 'poll' && (
            <div className="space-y-3 border border-border rounded-md p-3 bg-muted/20">
              <div className="space-y-1.5">
                <Label>Poll Question</Label>
                <Input data-testid="schedule-poll-question" value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} placeholder="Your question..." />
              </div>
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input data-testid={`schedule-poll-option-${i}`} value={opt} onChange={e => { const o = [...pollOptions]; o[i] = e.target.value; setPollOptions(o); }} placeholder={`Option ${i + 1}`} />
                  {i >= 2 && <Button type="button" variant="ghost" size="sm" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}><Trash size={14} /></Button>}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setPollOptions([...pollOptions, ''])} className="gap-1"><Plus size={14} /> Add Option</Button>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Text 1 (separate message)</Label>
            <Textarea data-testid="schedule-text-1" value={text1} onChange={e => setText1(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>Text 2 (with inline buttons)</Label>
            <Textarea data-testid="schedule-text-2" value={text2} onChange={e => setText2(e.target.value)} rows={2} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Inline Buttons</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setButtons([...buttons, { title: '', url: '' }])} className="gap-1"><Plus size={14} /> Add</Button>
            </div>
            {buttons.map((btn, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={btn.title} onChange={e => { const b = [...buttons]; b[i].title = e.target.value; setButtons(b); }} placeholder="Title" />
                <Input value={btn.url} onChange={e => { const b = [...buttons]; b[i].url = e.target.value; setButtons(b); }} placeholder="URL" />
                <Button type="button" variant="ghost" size="sm" onClick={() => setButtons(buttons.filter((_, j) => j !== i))}><Trash size={14} /></Button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
              <div><Label>Show &ldquo;Please click below the Buttons :&rdquo; label</Label><p className="text-xs text-muted-foreground">When OFF, only buttons are sent (no label text)</p></div>
              <Switch data-testid="schedule-show-btn-label" checked={showBtnLabel} onCheckedChange={setShowBtnLabel} />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div><Label>Request Mobile Number</Label><p className="text-xs text-muted-foreground">Include "Share Mobile Number" button</p></div>
            <Switch data-testid="schedule-request-mobile" checked={requestMobile} onCheckedChange={setRequestMobile} />
          </div>
        </div>
        <Button data-testid="schedule-submit-btn" type="submit" disabled={submitting} className="gap-2">
          <CalendarBlank size={18} /> {submitting ? 'Scheduling...' : 'Schedule Broadcast'}
        </Button>
      </form>
      {scheduled.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium" style={{ fontFamily: 'Cabinet Grotesk' }}>Scheduled Broadcasts</h3>
          <div className="border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead><tr className="bg-muted/50 border-b border-border"><th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Scheduled At</th><th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Content</th><th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Status</th><th className="text-right px-4 py-3 text-xs uppercase tracking-wider">Action</th></tr></thead>
              <tbody>
                {scheduled.map((s, i) => (
                  <tr key={s.id} className={`border-b border-border ${i % 2 ? 'bg-muted/20' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs">{s.scheduled_at?.replace('T', ' ').slice(0, 19)}</td>
                    <td className="px-4 py-3 text-xs truncate max-w-[200px]">{s.media_type === 'poll' ? `📊 ${s.poll_question || 'Poll'}` : s.text_1 || s.media_type || 'N/A'}</td>
                    <td className="px-4 py-3"><Badge variant={s.status === 'pending' ? 'secondary' : s.status === 'completed' ? 'default' : 'destructive'} className="text-xs">{s.status}</Badge></td>
                    <td className="px-4 py-3 text-right">{s.status === 'pending' && <Button variant="ghost" size="sm" onClick={() => deleteScheduled(s.id)} className="text-destructive"><Trash size={14} /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
